/**
 * Admin Settings Module
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded } from '../ui/dialogs.js';

interface SettingMeta {
  type: 'string' | 'boolean' | 'password' | 'int' | 'select';
  label: string;
  description?: string;
  optional?: boolean;
  readonly?: boolean;
  min?: number;
  max?: number;
  pattern?: string;
  placeholder?: string;
  options?: string[];
}

interface SettingGroup {
  label: string;
  settings: Record<string, SettingMeta>;
}

interface SettingKey {
  key: string;
  type: string;
  optional: boolean;
  readonly: boolean;
  meta: SettingMeta;
}

function generateAdminSettingInput(
  key: string,
  meta: SettingMeta,
  value: unknown
): string {
  const type = meta.type;
  const label = meta.label;
  const description = meta.description || '';
  const isOptional = meta.optional || false;
  const isReadonly = meta.readonly || false;
  const isEnabled = !isOptional || (value !== null && value !== undefined && value !== '');
  const disabledAttr = (!isEnabled || isReadonly) ? 'disabled' : '';

  // Build help icon if description exists
  const helpIcon = description
    ? `<span class="help-icon">?<span class="help-text">${escapeHtml(description)}</span></span>`
    : '';

  // Build optional toggle checkbox (not for readonly settings)
  const optionalToggle = (isOptional && !isReadonly)
    ? `<input type="checkbox" class="optional-toggle" data-key="${escapeAttr(key)}" ${isEnabled ? 'checked' : ''}>`
    : '';

  // Extra attributes for validation
  const attrs: string[] = [];
  if (meta.min !== undefined) attrs.push(`min="${meta.min}"`);
  if (meta.max !== undefined) attrs.push(`max="${meta.max}"`);
  if (meta.pattern) attrs.push(`pattern="${escapeAttr(meta.pattern)}"`);
  if (meta.placeholder) attrs.push(`placeholder="${escapeAttr(meta.placeholder)}"`);
  const extraAttrs = attrs.join(' ');

  let inputHtml: string;
  if (isReadonly && type !== 'boolean') {
    // Readonly non-boolean: display as plain text
    inputHtml = `<span class="setting-value" id="${escapeAttr(key)}">${escapeHtml(String(value ?? ''))}</span>`;
  } else if (type === 'boolean') {
    inputHtml = `<input type="checkbox" id="${escapeAttr(key)}" name="${escapeAttr(key)}" ${value ? 'checked' : ''} ${disabledAttr}>`;
  } else if (type === 'select') {
    const options = meta.options || [];
    let opts = '<option value="">(none)</option>';
    for (const opt of options) {
      const selected = opt === String(value || '') ? 'selected' : '';
      opts += `<option value="${escapeAttr(opt)}" ${selected}>${escapeHtml(opt)}</option>`;
    }
    inputHtml = `<select id="${escapeAttr(key)}" name="${escapeAttr(key)}" ${disabledAttr}>${opts}</select>`;
  } else if (type === 'password') {
    inputHtml = `<input type="password" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value || ''))}" autocomplete="new-password" ${disabledAttr} ${extraAttrs}>`;
  } else if (type === 'int') {
    inputHtml = `<input type="number" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value ?? ''))}" ${disabledAttr} ${extraAttrs}>`;
  } else {
    inputHtml = `<input type="text" id="${escapeAttr(key)}" name="${escapeAttr(key)}" value="${escapeAttr(String(value || ''))}" ${disabledAttr} ${extraAttrs}>`;
  }

  const rowClass = isReadonly ? 'setting-row setting-readonly' : 'setting-row';
  return `<div class="${rowClass}">
    <label for="${escapeAttr(key)}">${escapeHtml(label)}${helpIcon}</label>
    ${optionalToggle}
    ${inputHtml}
  </div>`;
}

function toggleOptionalSetting(key: string, checkbox: HTMLInputElement): void {
  const input = document.getElementById(key) as HTMLInputElement | null;
  if (input) {
    input.disabled = !checkbox.checked;
    if (!checkbox.checked) {
      if (input.type === 'checkbox') {
        input.checked = false;
      } else {
        input.value = '';
      }
    }
  }
}

/**
 * Open admin settings dialog
 */
export async function adminSettings(): Promise<void> {
  declareLoading();
  const response = await apiCall(api.getAdminSettings(), 'Failed to Load Settings');
  declareLoaded();
  if (!response) return;

  {

    const schema = response.schema as Record<string, SettingGroup>;
    const values = response.values as Record<string, unknown>;

    let form = '';
    const settingKeys: SettingKey[] = [];

    for (const [, group] of Object.entries(schema)) {
      form += `<fieldset><legend>${escapeHtml(group.label)}</legend>`;
      for (const [key, meta] of Object.entries(group.settings)) {
        settingKeys.push({
          key, type: meta.type, optional: meta.optional || false,
          readonly: meta.readonly || false, meta,
        });
        form += generateAdminSettingInput(key, meta, values[key]);
      }
      form += '</fieldset>';
    }

    $('<div></div>')
      .appendTo('body')
      .html('<div><h4>Admin Settings</h4>' + form + '</div>')
      .dialog({
        modal: true,
        title: 'Admin Settings',
        zIndex: 10000,
        autoOpen: true,
        width: 600,
        maxHeight: $(window).height()! * 0.8,
        resizable: true,
        buttons: {
          Save: async function () {
            const settings: Record<string, unknown> = {};
            const errors: string[] = [];
            settingKeys.forEach(({ key, type, optional, readonly: ro, meta }) => {
              if (ro) return; // Never send readonly fields
              const input = document.getElementById(key) as HTMLInputElement | null;
              // If optional and disabled, send null to clear the setting
              if (optional && input && input.disabled) {
                settings[key] = null;
              } else if (type === 'boolean') {
                settings[key] = $('#' + key).is(':checked');
              } else if (type === 'int') {
                const val = $('#' + key).val() as string;
                if (val === '') {
                  settings[key] = null;
                } else {
                  const n = parseInt(val);
                  if (isNaN(n)) {
                    errors.push(`${meta.label}: must be a number`);
                  } else {
                    if (meta.min !== undefined && n < meta.min)
                      errors.push(`${meta.label}: minimum is ${meta.min}`);
                    if (meta.max !== undefined && n > meta.max)
                      errors.push(`${meta.label}: maximum is ${meta.max}`);
                    settings[key] = n;
                  }
                }
              } else {
                const val = $('#' + key).val() as string;
                if (meta.pattern && val && !new RegExp(meta.pattern).test(val))
                  errors.push(`${meta.label}: invalid format`);
                settings[key] = val;
              }
            });
            if (errors.length > 0) {
              alert(errors.join('\n'));
              return;
            }
            declareLoading();
            const result = await apiCall(api.setAdminSettings({ settings }), 'Failed to Save Settings');
            declareLoaded();
            if (result !== null) $(this).dialog('close');
          },
          Cancel: function () {
            $(this).dialog('close');
          },
        },
        close: function () {
          $(this).remove();
        },
        open: function () {
          // Bind optional toggle handlers
          $('.optional-toggle').on('change', function () {
            const key = $(this).data('key') as string;
            toggleOptionalSetting(key, this as HTMLInputElement);
          });
        },
      });
  }
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).adminSettings = adminSettings;
  (window as unknown as Record<string, unknown>).toggleOptionalSetting = toggleOptionalSetting;
}
